import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// A friendly banner that surfaces from day 5 of the free trial with a
// one-tap subscribe button so no student experiences a hard cutoff on
// day 8.  Dismissible per-session via sessionStorage.
export default function TrialNudgeBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/subscription/status`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) setStatus(await r.json());
      } catch { /* ignore */ }
    };
    load();
    if (sessionStorage.getItem('trial_nudge_dismissed') === '1') setDismissed(true);
  }, []);

  if (dismissed || !status?.trial_active) return null;
  if (status?.subscription_plan) return null; // already subscribed, no nudge

  // Days remaining in trial
  const endMs = status.trial_ends_at ? new Date(status.trial_ends_at).getTime() : 0;
  const nowMs = Date.now();
  const daysLeft = Math.max(0, Math.ceil((endMs - nowMs) / (1000 * 60 * 60 * 24)));
  // Only show from day 5 onwards (i.e., 3 or fewer days remaining)
  if (daysLeft > 3) return null;

  const dismiss = () => {
    sessionStorage.setItem('trial_nudge_dismissed', '1');
    setDismissed(true);
  };

  const urgent = daysLeft <= 1;

  return (
    <div
      className={`mb-6 rounded-xl border p-4 relative flex items-center gap-3 ${
        urgent
          ? 'bg-gradient-to-r from-red-500/10 via-orange-500/10 to-amber-500/10 border-red-500/40'
          : 'bg-gradient-to-r from-indigo-500/10 via-fuchsia-500/10 to-emerald-500/10 border-indigo-500/40'
      }`}
      data-testid="trial-nudge-banner"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${urgent ? 'bg-red-500' : 'bg-indigo-500'}`}>
        <Sparkles className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">
          {urgent
            ? daysLeft === 0
              ? 'Your free trial ends today'
              : 'Only 1 day left in your free trial'
            : `${daysLeft} days left in your free trial`}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Subscribe now to keep your progress, streaks and AI-narrated concepts — plans start at ₹399/month.
        </p>
      </div>
      <Button
        size="sm"
        onClick={() => navigate('/subscription')}
        className={urgent ? 'bg-red-500 hover:bg-red-600 text-white' : ''}
        data-testid="trial-nudge-subscribe"
      >
        Subscribe now
      </Button>
      <button
        onClick={dismiss}
        className="text-muted-foreground hover:text-foreground p-1"
        title="Dismiss for this session"
        data-testid="trial-nudge-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
